import {Button, closeCurrentDialog, openDeleteDialog, openDialog} from 'platform/components';
import {Box, Heading, HStack, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback} from 'react';

import {isNil} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {useDeleteCustomerServiceContractMutation} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  DmsDataGrid,
  type DmsDataGridProps,
  handleApiError,
  refreshDmsDatagrid,
  Section,
  useCustomerUrl,
  usePermissions,
} from '@dms/shared';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {CustomerOrder} from './components/CustomerOrder';

const CUSTOMER_SERVICE_CONTRACT_GRID_CODE = 'customer-service-contract';

export function CustomerOrders(props: Pick<DmsDataGridProps, 'maximization'>) {
  const {customerId} = useCustomerUrl();

  const [deleteCustomerOrder] = useDeleteCustomerServiceContractMutation();

  const [canCreateCustomerOrder] = usePermissions({permissionKeys: ['createCustomerOrder']});

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => ({
      ...filter,
      customerId,
    }),
    [customerId]
  );

  const onCustomerOrderSubmitSuccess = () => {
    refreshDmsDatagrid(CUSTOMER_SERVICE_CONTRACT_GRID_CODE);
  };

  const handleNewCustomerOrder = () => {
    openDialog(
      <CustomerOrder
        customerId={customerId}
        onClose={closeCurrentDialog}
        onSubmitSuccess={onCustomerOrderSubmitSuccess}
        data-testid={testIds.customer.detail('customerOrder')}
      />,
      {
        title: i18n.t('entity.customer.customerOrders.createTitle'),
        withAdditionalFooter: true,
      }
    );
  };

  const actionCallback: ActionCallback = ({actionKey, rowId, refreshData}) => {
    const serviceContractId = isArray(rowId) ? rowId[0] : rowId;
    if (isNil(serviceContractId)) {
      throw new Error('Not able to call any action, serviceContractId is not defined');
    }
    match(actionKey)
      .with('redirectDetail', () =>
        openDialog(
          <CustomerOrder
            customerId={customerId}
            serviceContractId={serviceContractId}
            submitButtonTitle={i18n.t('general.labels.edit')}
            onClose={closeCurrentDialog}
            onSubmitSuccess={onCustomerOrderSubmitSuccess}
            data-testid={testIds.customer.detail('customerOrder')}
          />,
          {title: i18n.t('general.labels.edit'), withAdditionalFooter: true}
        )
      )
      .with('delete', () =>
        openDeleteDialog({
          text: i18n.t('entity.customer.customerOrders.confirmDelete'),
          onConfirm: () => {
            deleteCustomerOrder({customerId, serviceContractId})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError);
          },
        })
      );
  };

  return (
    <Section>
      <VStack spacing={4}>
        <HStack justify="space-between">
          <Heading size={3}>{i18n.t('entity.customer.customerOrders.title')}</Heading>
          <Button
            isDisabled={!canCreateCustomerOrder}
            variant="primary"
            title={i18n.t('general.labels.createNew')}
            onClick={handleNewCustomerOrder}
          />
        </HStack>

        <Box height={175}>
          <DmsDataGrid
            maximization={props.maximization}
            // DG must be re-rendered on folder change to update the query modifier
            // eslint-disable-next-line eslint-js/no-restricted-syntax
            key="customer-orders"
            data-testid={CUSTOMER_SERVICE_CONTRACT_GRID_CODE}
            gridCode={CUSTOMER_SERVICE_CONTRACT_GRID_CODE}
            queryModifier={queryModifier}
            actionCallback={actionCallback}
            disableAllActions={!canCreateCustomerOrder}
          />
        </Box>
      </VStack>
    </Section>
  );
}
